import {
  equipmentBoxId,
  problemEquipmentId,
  problemTimerId,
  problemWaterSourceId,
  problemZoneId,
} from '@lib/dto/irrigation.dto'
import type {
  IrrigationEquipmentDto,
  IrrigationProblemDto,
  IrrigationSiteDto,
  IrrigationTimerDto,
  ZoneDto,
} from '@lib/dto/irrigation.dto'
import type { SiteMapAsOfDto } from '@lib/dto/site-history.dto'
import type { IrrigationProblemTypeDto } from '@lib/dto/irrigation-problem-type.dto'
import {
  IRRIGATION_EQUIPMENT_KIND_LABELS,
  PROBLEM_SUBJECT_FOR_EQUIPMENT,
  WATER_SOURCE_KIND_LABELS,
  type IrrigationProblemSubject,
} from '@lib/enums/irrigation.enum'
import {
  timerName,
  zoneName,
  zoneNameInSentence,
  zoneNumbersText,
  zonesName,
} from '@lib/helpers/irrigation-names'

/*
 * What the survey calls each record, in the operator's words: "Clubhouse 4",
 * "Timer A", "Valve, zone 4". Kept in one place so the map, the sheets and the
 * zone picker never disagree about a name.
 */

// Shared with the proposal builder, so a bid and the survey name zones alike.
export { timerName, zoneName, zoneNameInSentence }

/** Zones in the order a technician walks them: by timer, then by number. */
export function zonesInOrder(site: IrrigationSiteDto): ZoneDto[] {
  const timerOrder = new Map(
    site.timers.map((timer, index) => [timer.id, index]),
  )
  return [...site.zones].sort(
    (a, b) =>
      (timerOrder.get(a.timerId) ?? 0) - (timerOrder.get(b.timerId) ?? 0) ||
      a.number - b.number,
  )
}

/**
 * The park (area) a timer stands in, when its name does not already say it:
 * "Bungalows" beside "Timer 2", nothing beside "Bungalows timer".
 */
export function timerParkNote(
  timer: IrrigationTimerDto,
  timers: readonly IrrigationTimerDto[],
): string | null {
  if (timer.areaName === null) return null
  const name = timerName(timer, timers).toLowerCase()
  return name.includes(timer.areaName.toLowerCase()) ? null : timer.areaName
}

/**
 * The site's zones by timer, for the "Walking" picker on a site with several:
 * timers in the same park together, parks in name order and timers in no park
 * last, each labeled with its park when its name does not say it.
 */
export function zonesByTimer(
  site: IrrigationSiteDto,
): { timerId: string; label: string; zones: ZoneDto[] }[] {
  const zones = zonesInOrder(site)
  const recorded = new Map(site.timers.map((timer, index) => [timer.id, index]))
  const park = (timer: IrrigationTimerDto) => timer.areaName ?? '\uffff'
  return [...site.timers]
    .sort(
      (a, b) =>
        park(a).localeCompare(park(b)) ||
        (recorded.get(a.id) ?? 0) - (recorded.get(b.id) ?? 0),
    )
    .map((timer) => {
      const note = timerParkNote(timer, site.timers)
      const name = timerName(timer, site.timers)
      return {
        timerId: timer.id,
        label: note === null ? name : `${name} · ${note}`,
        zones: zones.filter((zone) => zone.timerId === timer.id),
      }
    })
    .filter((group) => group.zones.length > 0)
}

/** What sits in a valve box, valves first and in zone order. */
export function boxContents(
  boxId: string,
  site: IrrigationSiteDto,
): IrrigationEquipmentDto[] {
  const order = new Map(
    zonesInOrder(site).map((zone, index) => [zone.id, index]),
  )
  const rank = (item: IrrigationEquipmentDto) => {
    if (item.kind !== 'VALVE' || item.zoneId === null) {
      return Number.MAX_SAFE_INTEGER
    }
    return order.get(item.zoneId) ?? Number.MAX_SAFE_INTEGER
  }
  return site.equipment
    .filter((item) => equipmentBoxId(item) === boxId)
    .sort((a, b) => rank(a) - rank(b))
}

/**
 * The zones whose valves are in a box, named a timer at a time: "Zones 2–5",
 * "Bungalows 17 and 23", "Bungalows 4; Founders 23". Null when no valve in
 * it has a zone yet.
 */
export function boxZonesName(
  boxId: string,
  site: IrrigationSiteDto,
): string | null {
  const byTimer = new Map<string, number[]>()
  for (const item of boxContents(boxId, site)) {
    if (item.kind !== 'VALVE') continue
    const zone = site.zones.find((entry) => entry.id === item.zoneId)
    if (zone === undefined) continue
    byTimer.set(zone.timerId, [
      ...(byTimer.get(zone.timerId) ?? []),
      zone.number,
    ])
  }
  if (byTimer.size === 0) return null
  return [...byTimer]
    .map(([timerId, numbers]) => zonesName(timerId, numbers, site.timers))
    .join('; ')
}

export function equipmentName(
  item: IrrigationEquipmentDto,
  site: IrrigationSiteDto,
): string {
  if (item.kind === 'OTHER') return item.name ?? 'Other'
  const kind = IRRIGATION_EQUIPMENT_KIND_LABELS[item.kind]
  if (item.kind === 'VALVE_BOX') {
    const zones = boxZonesName(item.id, site)
    if (zones !== null) return `${kind}, ${lowerFirst(zones)}`
    const valves = boxContents(item.id, site).filter(
      (entry) => entry.kind === 'VALVE',
    ).length
    if (valves === 0) return kind
    return `${kind}, ${String(valves)} ${valves === 1 ? 'valve' : 'valves'}`
  }
  if (item.kind === 'VALVE') {
    const zone = site.zones.find((entry) => entry.id === item.zoneId)
    return zone === undefined
      ? `${kind}, zone not known`
      : `${kind}, ${zoneNameInSentence(zone, site.timers)}`
  }
  return kind
}

/**
 * What a pin reads of a site: the live survey, or the flat rows a past
 * visit's map draws. The two stay apart. A valve's zone is required on the
 * live record and present on every past row; a box has neither.
 */
type GlyphEquipment =
  | IrrigationEquipmentDto
  | SiteMapAsOfDto['equipment'][number]

type GlyphSite =
  | Pick<IrrigationSiteDto, 'zones' | 'equipment'>
  | Pick<SiteMapAsOfDto, 'zones' | 'equipment'>

/**
 * Zone ids of the valves in one box.
 *
 * A live valve and a past visit's flat row both carry a zone, but they are
 * not one type: the live row is a variant, and the past row has a zone on
 * every kind. `siteId` is only on the live row, which is what tells them apart.
 */
function valveZoneIds(site: GlyphSite, boxId: string): (string | null)[] {
  const zoneIds: (string | null)[] = []
  for (const item of site.equipment) {
    if (item.kind !== 'VALVE') continue
    if ('siteId' in item) {
      if (item.boxId === boxId) zoneIds.push(item.zoneId)
      continue
    }
    if (item.boxId === boxId) zoneIds.push(item.zoneId)
  }
  return zoneIds
}

/**
 * A valve box's pin: the numbers of the zones whose valves are in it ("2–5",
 * "17·23"), or how many valves when that would not fit or none has a zone.
 */
function boxGlyph(boxId: string, site: GlyphSite): string {
  const zoneIds = valveZoneIds(site, boxId)
  const numbers = zoneIds.flatMap((zoneId) => {
    const zone = site.zones.find((entry) => entry.id === zoneId)
    return zone === undefined ? [] : [zone.number]
  })
  if (zoneIds.length === 0) return 'VB'
  const text = zoneNumbersText(numbers).replace(/, | and /g, '·')
  return numbers.length > 0 && text.length <= 5 ? text : String(zoneIds.length)
}

/**
 * The short text inside a pin: a valve's zone number, a valve box's zones,
 * or a kind's initials.
 */
export function equipmentGlyph(item: GlyphEquipment, site: GlyphSite): string {
  switch (item.kind) {
    case 'VALVE': {
      const zone = site.zones.find((entry) => entry.id === item.zoneId)
      return zone === undefined ? 'V' : String(zone.number)
    }
    case 'VALVE_BOX':
      return boxGlyph(item.id, site)
    case 'MASTER_VALVE':
      return 'MV'
    case 'BACKFLOW':
      return 'BF'
    case 'BALL_VALVE':
      return 'BV'
    case 'SENSOR':
      return 'S'
    case 'WELL_COMPONENT':
      return 'W'
    case 'OTHER':
      return '•'
  }
}

/** What a problem is on, as a problem type names it. */
export function problemSubject(
  problem: IrrigationProblemDto,
  site: IrrigationSiteDto,
): IrrigationProblemSubject {
  const zoneId = problemZoneId(problem)
  if (zoneId !== null) return problem.position === null ? 'ZONE' : 'ZONE_SPOT'
  if (problemTimerId(problem) !== null) return 'TIMER'
  if (problemWaterSourceId(problem) !== null) return 'WATER_SOURCE'
  const equipmentId = problemEquipmentId(problem)
  const item = site.equipment.find((entry) => entry.id === equipmentId)
  return item === undefined
    ? 'OTHER_EQUIPMENT'
    : PROBLEM_SUBJECT_FOR_EQUIPMENT[item.kind]
}

/** "Bad nozzle ×3", or the start of an "Other" problem's notes. */
export function problemTitle(
  problem: IrrigationProblemDto,
  types: readonly IrrigationProblemTypeDto[],
): string {
  const type = types.find((entry) => entry.id === problem.typeId)
  const name =
    type?.name ?? problem.notes?.split('\n')[0]?.slice(0, 40) ?? 'Other problem'
  return problem.count > 1 ? `${name} ×${String(problem.count)}` : name
}

/** A water source's name: its own label, else what kind it is. */
export function waterSourceName(
  kind: keyof typeof WATER_SOURCE_KIND_LABELS,
  label: string | null,
): string {
  return label ?? WATER_SOURCE_KIND_LABELS[kind]
}

/**
 * The same inside a sentence: "on city meter", but "on Meter by the
 * clubhouse". A name somebody typed keeps its capital.
 */
export function waterSourceNameInSentence(
  kind: keyof typeof WATER_SOURCE_KIND_LABELS,
  label: string | null,
): string {
  return label ?? lowerFirst(WATER_SOURCE_KIND_LABELS[kind])
}

/**
 * Drops the first letter's capital, for text that starts with the app's own
 * words ("Master valve on Bungalows" after "Next:"), where lowercasing it all
 * would lowercase a name somebody typed.
 */
export function lowerFirst(text: string): string {
  return text.charAt(0).toLowerCase() + text.slice(1)
}
